import axios from "axios";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import TopBar from "../components/TopBar";
import VideosSection from "../components/VideosSection";
import DevelopmentSection from "../components/DevelopmentSection";
import SkillsTracking from "../components/SkillsTracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = ["info", "trainings", "videos", "development", "skills", "notes", "stats"];

const STATUS_BADGE = {
  present: { label: "present", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  absent: { label: "absent", cls: "bg-[#e05555]/15 text-[#e05555] border-[#e05555]/30" },
  late: { label: "late", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  excused: { label: "excused", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
};

export default function PlayerProfilePage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const password = localStorage.getItem("st_admin_pw") || "";
  const headers = { "X-Admin-Password": password };

  const [player, setPlayer] = useState(null);
  const [tab, setTab] = useState("info");
  const [trainings, setTrainings] = useState([]);
  const [stats, setStats] = useState({ present: 0, absent: 0, late: 0, excused: 0, total: 0 });
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/players/${id}`, { headers }).then((r) => setPlayer(r.data));
    axios.get(`${API}/admin/players/${id}/trainings`, { headers }).then((r) => {
      setTrainings(r.data.trainings || []);
      setStats(r.data.stats || stats);
    });
    axios.get(`${API}/admin/players/${id}/notes`, { headers }).then((r) => setNotes(r.data));
    axios.get(`${API}/admin/players/${id}/videos`, { headers }).then((r) => setVideos(r.data));
    // eslint-disable-next-line
  }, [id]);

  const addNote = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/admin/players/${id}/notes`,
        { content: newNote },
        { headers }
      );
      setNotes((n) => [r.data, ...n]);
      setNewNote("");
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    await axios.delete(`${API}/admin/players/${id}/notes/${noteId}`, { headers });
    setNotes((n) => n.filter((x) => x.id !== noteId));
  };

  if (!player) {
    return (
      <div className="min-h-screen bg-[#080808] text-white">
        <TopBar />
        <div className="max-w-3xl mx-auto px-4 py-10 text-neutral-500">...</div>
      </div>
    );
  }

  const positionLabel = lang === "es" ? t(`pos_${player.position}`) : player.position;
  const attendanceRate =
    stats.total > 0
      ? Math.round(((stats.present + stats.late * 0.5) / stats.total) * 100)
      : 0;

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      <TopBar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <Link
          to="/admin"
          data-testid="back-to-admin"
          className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-[#e8559a] transition-colors mb-4"
        >
          ← {t("back")}
        </Link>

        {player.access_token && (
          <PortalLinkBar token={player.access_token} t={t} />
        )}

        {/* Hero */}
        <div className="rounded-2xl bg-gradient-to-br from-[#161616] to-[#1a0e16] border border-[#e8559a]/20 p-6 sm:p-8 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="flex-shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-[#e8559a]/10 border border-[#e8559a]/40 flex items-center justify-center">
              <span className="font-bebas text-5xl sm:text-6xl text-[#e8559a] tracking-widest leading-none">
                {player.player_number}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#e8559a]/80 mb-1">
                # {t("player_number_label")}
              </div>
              <h1 className="font-bebas text-4xl sm:text-5xl tracking-wider leading-tight">
                {player.first_name} {player.last_name}
              </h1>
              <div className="mt-3 flex flex-wrap gap-2 text-sm">
                <span className="px-3 py-1 rounded-full bg-[#0f0f0f] border border-[#252525] text-neutral-300">
                  ⚽ {positionLabel}
                </span>
                <span className="px-3 py-1 rounded-full bg-[#0f0f0f] border border-[#252525] text-neutral-300 capitalize">
                  🦵 {player.dominant_foot}
                </span>
                <span className="px-3 py-1 rounded-full bg-[#0f0f0f] border border-[#252525] text-neutral-300">
                  🎂 {player.age} {lang === "es" ? "años" : "yrs"}
                </span>
                <span className="px-3 py-1 rounded-full bg-[#0f0f0f] border border-[#252525] text-neutral-300 capitalize">
                  {lang === "es" ? t(`exp_${player.experience}`) : player.experience}
                </span>
              </div>
            </div>
            <div className="flex-shrink-0 grid grid-cols-2 gap-3 w-full sm:w-auto">
              <Stat label={t("total_sessions")} value={stats.total} />
              <Stat label={t("attendance_rate")} value={`${attendanceRate}%`} accent />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1.5 mb-5 border-b border-[#252525]">
          {TABS.map((k) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`tab-${k}`}
              className={`px-4 py-2.5 -mb-px text-sm font-medium transition-all border-b-2 ${
                tab === k
                  ? "text-[#e8559a] border-[#e8559a]"
                  : "text-neutral-400 border-transparent hover:text-white"
              }`}
            >
              {k === "info" && "📋 "}
              {k === "trainings" && "📅 "}
              {k === "videos" && "🎬 "}
              {k === "development" && "💡 "}
              {k === "skills" && "📊 "}
              {k === "notes" && "📝 "}
              {k === "stats" && "📊 "}
              {t(`tab_${k}`)}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === "info" && <InfoTab player={player} t={t} lang={lang} />}
        {tab === "trainings" && (
          <TrainingsTab trainings={trainings} t={t} />
        )}
        {tab === "videos" && (
          <VideosSection
            mode="admin"
            playerId={id}
            apiPrefix={`/admin/players/${id}`}
            fileUrlFor={(vid) => `${API}/admin/videos/${vid}/file`}
            authHeaders={headers}
          />
        )}
        {tab === "development" && (
          <DevelopmentSection
            mode="admin"
            playerId={id}
            fileUrlFor={(vid) => `${API}/admin/videos/${vid}/file`}
            authHeaders={headers}
            videos={videos}
          />
        )}
        {tab === "skills" && (
          <SkillsTracking mode="admin" playerId={id} authHeaders={headers} />
        )}
        {tab === "notes" && (
          <NotesTab
            notes={notes}
            newNote={newNote}
            setNewNote={setNewNote}
            saving={saving}
            onAdd={addNote}
            onDelete={deleteNote}
            t={t}
          />
        )}
        {tab === "stats" && <StatsTab stats={stats} attendanceRate={attendanceRate} t={t} />}
      </main>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${accent ? "bg-[#e8559a]/10 border-[#e8559a]/40" : "bg-[#0f0f0f] border-[#252525]"}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">{label}</div>
      <div className={`font-bebas text-3xl tracking-widest leading-none mt-1 ${accent ? "text-[#e8559a]" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function PortalLinkBar({ token, t }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/p/${token}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  const waHref = `https://wa.me/?text=${encodeURIComponent(`Soccer Troop · Portal del padre: ${url}`)}`;
  return (
    <div className="rounded-xl bg-[#161616] border border-[#e8559a]/30 px-4 py-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-2.5" data-testid="portal-link-bar">
      <div className="flex-shrink-0 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#e8559a]">
        🔗 Portal del padre
      </div>
      <div className="flex-1 min-w-0 bg-[#0a0a0a] border border-[#252525] rounded-lg px-3 py-1.5 truncate text-xs text-neutral-300 font-mono">
        {url}
      </div>
      <div className="flex gap-2">
        <button
          onClick={copy}
          data-testid="copy-portal-link"
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${copied ? "bg-emerald-500 text-white" : "bg-[#e8559a] hover:bg-[#c9407f] text-white"}`}
        >
          {copied ? `✓ ${t("copied")}` : `⧉ ${t("copy_link")}`}
        </button>
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-1.5 rounded-md bg-[#0f0f0f] border border-[#252525] hover:border-[#25d366]/60 text-xs text-neutral-200 transition-colors"
        >
          💬 WhatsApp
        </a>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-[#252525] last:border-b-0">
      <span className="text-xs uppercase tracking-wider text-neutral-500">{label}</span>
      <span className="text-sm text-white text-right">{value || "—"}</span>
    </div>
  );
}

function InfoTab({ player, t, lang }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="info-tab">
      <Card title={`📋 ${t("section_player")}`}>
        <Row label={t("first_name")} value={player.first_name} />
        <Row label={t("last_name")} value={player.last_name} />
        <Row label={t("date_of_birth")} value={player.date_of_birth} />
        <Row label={t("age")} value={player.age} />
        <Row label={t("gender")} value={lang === "es" ? t(`gender_${player.gender}`) : player.gender} />
        <Row label={t("primary_position")} value={lang === "es" ? t(`pos_${player.position}`) : player.position} />
        <Row label={t("dominant_foot")} value={lang === "es" ? t(`foot_${player.dominant_foot}`) : player.dominant_foot} />
        <Row label={t("experience")} value={lang === "es" ? t(`exp_${player.experience}`) : player.experience} />
        <Row label={t("current_club")} value={player.current_club} />
      </Card>

      <Card title={`👨‍👩‍👧 ${t("section_parent")}`}>
        <Row label={t("parent_full_name")} value={player.parent_full_name} />
        <Row label={t("relationship")} value={lang === "es" ? t(`rel_${player.relationship}`) : player.relationship} />
        <Row label={t("email")} value={<a href={`mailto:${player.email}`} className="text-[#e8559a] hover:underline">{player.email}</a>} />
        <Row label={t("phone")} value={<a href={`https://wa.me/${(player.phone||'').replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="text-[#e8559a] hover:underline">{player.phone}</a>} />
        <Row label={t("emergency_contact")} value={`${player.emergency_contact?.name || ""} · ${player.emergency_contact?.phone || ""}`} />
      </Card>

      <Card title={`🏥 ${t("medical")}`}>
        <Row label={t("has_medical")} value={player.has_medical_condition ? t("yes") : t("no")} />
        {player.has_medical_condition && (
          <Row label={t("medical_details")} value={player.medical_condition_details} />
        )}
        <Row label={t("allergies")} value={player.allergies} />
        <Row label={t("medications")} value={player.medications} />
      </Card>

      <Card title={`✅ ${t("consent_status")}`}>
        {[
          ["marketing", "consent_marketing_title"],
          ["social_media", "consent_social_title"],
          ["insurance_waiver", "consent_insurance_title"],
          ["liability_waiver", "consent_liability_title"],
          ["general", "consent_general_title"],
        ].map(([k, labelKey]) => (
          <Row
            key={k}
            label={t(labelKey)}
            value={player.consent?.[k] ? <span className="text-emerald-400">✓ {t("accepted")}</span> : "—"}
          />
        ))}
        <Row label={t("guardian_printed")} value={player.guardian_printed_name} />
        <Row label={t("date_signed")} value={player.signed_date} />
      </Card>

      {player.signature_base64 && (
        <Card title={`✍️ ${t("signature_label")}`} className="lg:col-span-2">
          <div className="bg-white rounded-lg p-4 inline-block">
            <img src={player.signature_base64} alt="signature" className="max-h-32" />
          </div>
        </Card>
      )}
    </div>
  );
}

function Card({ title, children, className = "" }) {
  return (
    <div className={`rounded-2xl bg-[#161616] border border-[#252525] p-5 sm:p-6 ${className}`}>
      <h3 className="font-bebas text-xl tracking-widest text-white mb-3 pb-3 border-b border-[#252525]">
        {title}
      </h3>
      <div>{children}</div>
    </div>
  );
}

function TrainingsTab({ trainings, t }) {
  if (trainings.length === 0) {
    return (
      <div className="rounded-2xl bg-[#161616] border border-[#252525] p-10 text-center text-neutral-500" data-testid="trainings-tab-empty">
        {t("no_trainings")}
      </div>
    );
  }
  return (
    <div className="space-y-2.5" data-testid="trainings-tab">
      {trainings.map((tr) => {
        const status = tr.status;
        const badge = status ? STATUS_BADGE[status] : null;
        return (
          <div
            key={tr.id}
            className="rounded-xl bg-[#161616] border border-[#252525] p-4 flex flex-col sm:flex-row sm:items-center gap-3"
          >
            <div className="flex-shrink-0 w-16 text-center rounded-lg bg-[#0f0f0f] border border-[#252525] py-2">
              <div className="font-bebas text-2xl text-[#e8559a] tracking-widest leading-none">
                {(tr.date || "").slice(8, 10)}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
                {(tr.date || "").slice(5, 7)}/{(tr.date || "").slice(0, 4)}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-white">{tr.title}</div>
              <div className="text-xs text-neutral-400 mt-1">
                {tr.time} · {tr.location || "—"} · {tr.duration_minutes} min
              </div>
            </div>
            {badge ? (
              <span className={`flex-shrink-0 px-3 py-1 rounded-full border text-xs font-medium ${badge.cls}`}>
                {t(badge.label)}
              </span>
            ) : (
              <span className="flex-shrink-0 px-3 py-1 rounded-full border border-[#252525] text-xs text-neutral-500">
                {t("not_marked")}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function NotesTab({ notes, newNote, setNewNote, saving, onAdd, onDelete, t }) {
  return (
    <div data-testid="notes-tab">
      <div className="rounded-2xl bg-[#161616] border border-[#252525] p-4 sm:p-5 mb-4">
        <textarea
          rows={3}
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder={t("note_placeholder")}
          data-testid="note-textarea"
          className="w-full bg-[#0f0f0f] border border-[#252525] rounded-lg px-4 py-3 text-white placeholder:text-neutral-600 focus:border-[#e8559a] focus:outline-none resize-none"
        />
        <div className="mt-3 flex justify-end">
          <button
            onClick={onAdd}
            disabled={saving || !newNote.trim()}
            data-testid="add-note-btn"
            className="px-5 py-2 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] disabled:opacity-50 text-white font-bebas text-sm tracking-widest transition-colors"
          >
            ＋ {t("add_note")}
          </button>
        </div>
      </div>
      {notes.length === 0 ? (
        <div className="text-center py-10 text-neutral-500">{t("no_notes")}</div>
      ) : (
        <div className="space-y-3">
          {notes.map((n) => (
            <div
              key={n.id}
              data-testid={`note-${n.id}`}
              className="rounded-xl bg-[#161616] border border-[#252525] p-4 group relative"
            >
              <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-[0.2em] text-[#e8559a]/80">
                <span>●</span>
                {new Date(n.created_at).toLocaleDateString()} ·{" "}
                {new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
              <p className="text-sm text-neutral-200 leading-relaxed whitespace-pre-wrap">
                {n.content}
              </p>
              <button
                onClick={() => onDelete(n.id)}
                className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 text-[#e05555] hover:bg-[#e05555]/10 rounded-md p-1 transition-all"
                title={t("delete_note")}
              >
                🗑
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatsTab({ stats, attendanceRate, t }) {
  const cells = [
    { k: "present", emoji: "✅", color: "emerald" },
    { k: "absent", emoji: "❌", color: "red" },
    { k: "late", emoji: "🕒", color: "amber" },
    { k: "excused", emoji: "📋", color: "sky" },
  ];
  return (
    <div className="space-y-5" data-testid="stats-tab">
      <div className="rounded-2xl bg-gradient-to-br from-[#161616] to-[#1a0e16] border border-[#e8559a]/30 p-6 sm:p-8 text-center">
        <div className="text-xs uppercase tracking-[0.3em] text-[#e8559a]/80 mb-2">
          {t("attendance_rate")}
        </div>
        <div className="font-bebas text-7xl sm:text-8xl text-[#e8559a] tracking-widest leading-none">
          {attendanceRate}%
        </div>
        <div className="text-sm text-neutral-400 mt-3">
          {t("total_sessions")}: <span className="text-white font-bebas tracking-wider text-lg">{stats.total}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cells.map((c) => (
          <div key={c.k} className="rounded-xl bg-[#161616] border border-[#252525] p-5 text-center">
            <div className="text-3xl mb-2">{c.emoji}</div>
            <div className="font-bebas text-3xl text-white tracking-widest">{stats[c.k] || 0}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1">{t(c.k)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
